import {
  Grid,
  Box,
  Paper,
  Typography,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import { createRestaurantUserAsync } from './createRestaurantUserSlice';
import { useTranslation } from 'react-i18next';
import { restaurantUserValidationSchema } from './restaurantUserValidation';
import { updateRestaurantUserAsync } from './updateRestaurantUserSlice';
import { toast } from 'react-toastify';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  CircularProgress,
  Button,
} from '@mui/material';
import { RestaurantUser } from '../../../app/data/models/restaurantUsers/restaurantUser';
import { useEffect, useState } from 'react';
import Kitchen from '../../../app/data/models/kitchens/kitchen';
import api from '../../../app/api/api';
import { UserRoles } from '../../../app/utils/userRoles';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  RestaurantUserFormData,
  UpdateRestaurantUserFormData,
} from '../../../app/data/models/restaurantUsers/restaurantUserFormdata';
import ListTitleBox from '../../../app/components/list/ListTitleBox';
import { AxiosError } from 'axios';

interface Props {
  restaurantUser?: RestaurantUser;
  restaurantId: string;
  cancelEdit: () => void;
}

const RestaurantUserForm = ({
  restaurantUser,
  restaurantId,
  cancelEdit,
}: Props) => {
  const { control, handleSubmit, setValue } = useForm<RestaurantUserFormData>({
    mode: 'onTouched',
    resolver: yupResolver(restaurantUserValidationSchema),
    defaultValues: restaurantUser
      ? {
          firstName: restaurantUser.firstName,
          lastName: restaurantUser.lastName,
          email: restaurantUser.email,
          role: undefined as unknown as UserRoles,
        }
      : undefined,
  });

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { status } = useAppSelector((state) => state.createRestaurantUser);

  const [kitchens, setKitchens] = useState<Kitchen[] | undefined>(undefined);
  const [selectedKitchens, setSelectedKitchens] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState<UserRoles | undefined>(undefined);
  const [isLoadingKitchens, setIsLoadingKitchens] = useState(true);

  useEffect(() => {
    const fetchKitchens = async () => {
      try {
        setIsLoadingKitchens(true);
        const kitchens = await api.Kitchen.list(restaurantId.toString());
        setKitchens(kitchens);
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          console.error('Failed to fetch kitchens:', error.response?.data);
        } else {
          console.error('Failed to fetch kitchens:', error);
        }
      } finally {
        setIsLoadingKitchens(false);
      }
    };
    if (role === UserRoles.KitchenStaff && !kitchens) {
      fetchKitchens();
    }
  }, [role, kitchens, api]);

  useEffect(() => {
    const fetchUserRoles = async () => {
      if (!restaurantUser?.id || !restaurantId) return;
      try {
        setIsLoading(true);
        setError(null);
        const response = await api.RestaurantUser.userRoles(
          restaurantId,
          restaurantUser.id
        );
        setRole(response.role as UserRoles);
        setSelectedKitchens(response.kitchenIds);
        setValue('role', response.role);
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          console.error('Failed to fetch user roles:', error.response?.data);
        } else {
          console.error('Failed to fetch user roles:', error);
        }
        setError(t('restaurant-users.form.error.fetch-role'));
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserRoles();
  }, [restaurantUser, restaurantId, setValue]);

  const handleRoleChange = (role: UserRoles) => {
    setRole(role);
    if (role !== UserRoles.KitchenStaff) {
      setSelectedKitchens([]);
    }
  };

  const handleKitchenChange = (kitchenId: string) => {
    setSelectedKitchens((prev) => {
      const currentSelected = prev || [];
      return currentSelected.includes(kitchenId)
        ? currentSelected.filter((id) => id !== kitchenId)
        : [...currentSelected, kitchenId];
    });
  };

  const handleSubmitData = async (data: RestaurantUserFormData) => {
    try {
      if (restaurantUser) {
        // Update existing user
        const updateData: UpdateRestaurantUserFormData = {
          id: restaurantUser.id,
          email: restaurantUser.email,
          role: data.role,
          kitchenIds:
            role === UserRoles.KitchenStaff ? selectedKitchens : undefined,
        };
        await dispatch(
          updateRestaurantUserAsync({ restaurantId, data: updateData })
        );
        toast.success(t('restaurant-users.update.success'));
      } else {
        // Create new user
        const createData: RestaurantUserFormData = {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          role: data.role,
          kitchenIds:
            role === UserRoles.KitchenStaff ? selectedKitchens : undefined,
        };
        await dispatch(
          createRestaurantUserAsync({ restaurantId, data: createData })
        );
        toast.success(t('restaurant-users.create.success'));
      }
      cancelEdit();
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        console.error('Operation failed:', error.response?.data);
      } else {
        console.error('Operation failed:', error);
      }
      toast.error(t('common.error'));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', p: 3 }}>
        <Typography color="error" gutterBottom>
          {error}
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          startIcon={<RefreshIcon />}
        >
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  if (!role && restaurantUser) {
    return (
      <Box sx={{ textAlign: 'center', p: 3 }}>
        <Typography color="error">
          {t('restaurant-users.form.error.no-role')}
        </Typography>
      </Box>
    );
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit(handleSubmitData)();
  };

  return (
    <Box>
      <ListTitleBox
        title={
          restaurantUser
            ? t('restaurant-users.edit-title')
            : t('restaurant-users.create-title')
        }
        onPrimaryClick={() => handleSubmit(handleSubmitData)()}
        primaryButtonText={
          restaurantUser ? t('common.update') : t('common.create')
        }
        primaryButtonProps={{
          loading: status.includes('pending'),
          type: 'submit',
        }}
        onBackClick={cancelEdit}
      />
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleFormSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              {restaurantUser ? (
                <TextField
                  fullWidth
                  variant="outlined"
                  name="firstName"
                  label={t('restaurant-users.form.fields.first-name')}
                  defaultValue={restaurantUser.firstName}
                  disabled
                />
              ) : (
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      label={t('restaurant-users.form.fields.first-name')}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {restaurantUser ? (
                <TextField
                  fullWidth
                  variant="outlined"
                  name="lastName"
                  label={t('restaurant-users.form.fields.last-name')}
                  defaultValue={restaurantUser.lastName}
                  disabled
                />
              ) : (
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      label={t('restaurant-users.form.fields.last-name')}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              {restaurantUser ? (
                <TextField
                  fullWidth
                  variant="outlined"
                  name="email"
                  label={t('restaurant-users.form.fields.email')}
                  defaultValue={restaurantUser.email}
                  disabled
                />
              ) : (
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      label={t('restaurant-users.form.fields.email')}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>
                      {t('restaurant-users.form.fields.role')}
                    </InputLabel>
                    <Select
                      {...field}
                      label={t('restaurant-users.form.fields.role')}
                      onChange={(e) => {
                        field.onChange(e);
                        handleRoleChange(e.target.value as UserRoles);
                      }}
                    >
                      <MenuItem value={UserRoles.RestaurantManager}>
                        {t('restaurant-users.roles.manager')}
                      </MenuItem>
                      <MenuItem value={UserRoles.KitchenStaff}>
                        {t('restaurant-users.roles.kitchen')}
                      </MenuItem>
                      <MenuItem value={UserRoles.RestaurantWaiter}>
                        {t('restaurant-users.roles.waiter')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            {role === UserRoles.KitchenStaff && (
              <Grid item xs={12}>
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Typography
                    variant="subtitle1"
                    color="primary"
                    fontWeight="medium"
                  >
                    {t('restaurant-users.form.fields.kitchen-access')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {t('restaurant-users.form.fields.kitchen-access-helper')}
                  </Typography>
                </Box>
                <Paper sx={{ p: 2 }}>
                  <FormGroup>
                    {isLoadingKitchens ? (
                      <Box
                        sx={{ display: 'flex', justifyContent: 'center', p: 2 }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    ) : kitchens && kitchens.length > 0 ? (
                      (kitchens ?? []).map((kitchen) => (
                        <FormControlLabel
                          key={kitchen.id}
                          control={
                            <Checkbox
                              checked={selectedKitchens?.includes(kitchen.id)}
                              onChange={() => handleKitchenChange(kitchen.id)}
                              color="primary"
                            />
                          }
                          label={kitchen.name}
                        />
                      ))
                    ) : (
                      <Typography color="error" align="center">
                        {t('restaurant-users.form.fields.no-kitchens')}
                      </Typography>
                    )}
                  </FormGroup>
                </Paper>
              </Grid>
            )}
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default RestaurantUserForm;
